import React, { useEffect, useState } from "react";
import DirectionsResult = google.maps.DirectionsResult;
import { FaCar, FaTools } from "react-icons/fa";
import { Timestamp } from "firebase/firestore";
import { motion } from "framer-motion";
import ExclamationTriangleIcon from "@heroicons/react/24/outline/ExclamationTriangleIcon";
// Local
import MapWithDirections from "./MapWithDirections";
import { ExistingTask } from "../../models/task";
import { convertToReadableTimestampTime } from "../../assets/js/convertToReadableTimestamp";
import { ResourceTechnicianVehicle } from "../../Pages/Scheduling/SchedulingDetailContainer";
import { useSiteKeyDocStore } from "../../store/site-key-doc";
import { VroomStep } from "../../models/vroom";
import StyledMessage from "../StyledMessage";

interface SchedulingDetailResourceViewProps {
  allResources: ResourceTechnicianVehicle[];
  selectedResource: ResourceTechnicianVehicle | null;
  setSelectedTask: (task: ExistingTask | null) => void;
  assignUserButton: React.ReactNode;
  unroutableTasks: {
    taskId: string;
    reason: string;
    originalVehicleId?: string;
    originalTechnicianIds?: string[];
  }[];
  directionsResponses: Record<string, DirectionsResult>;
  setHoveredTask: (id: string | null) => void;
  children: {
    resourceStartEndMarkers: React.ReactNode;
    resourceMarkers: React.ReactNode;
  };
}

const SchedulingDetailResourceView = ({
  allResources,
  selectedResource,
  setSelectedTask,
  assignUserButton,
  unroutableTasks,
  directionsResponses,
  setHoveredTask,
  children,
}: SchedulingDetailResourceViewProps) => {
  const siteKeyDoc = useSiteKeyDocStore((state) => state.siteKeyDoc);
  const [resourceTasksList, setResourceTasksList] = useState<ExistingTask[]>(
    [],
  );

  useEffect(() => {
    if (selectedResource) {
      setResourceTasksList(selectedResource.taskList);
    } else {
      // get flat list of all resource tasks
      const allTasks: ExistingTask[] = [];
      allResources.forEach((resource) => {
        allTasks.push(...resource.taskList);
      });
      setResourceTasksList(allTasks);
    }
  }, [allResources, selectedResource]);

  const technicianDisplayNames = selectedResource?.technicians
    .map((tech) => tech.displayName)
    .join(", ");
  const timelineSteps = convertVroomStepsToTimelineSteps(
    selectedResource?.vroomRoute?.steps ?? [],
    resourceTasksList,
  );

  const UnroutableTasksWarning = () => {
    if (!selectedResource) return null;

    let unroutableForResource: any[] = [];
    if (selectedResource.vehicle !== null) {
      // Vehicles mode
      unroutableForResource = unroutableTasks.filter(
        (ut) => ut.originalVehicleId === selectedResource.vehicle?.id,
      );
    } else if (selectedResource.technicians.length > 0) {
      // Technicians mode
      unroutableForResource = unroutableTasks.filter(
        (ut) => ut.originalVehicleId === selectedResource.technicians[0]?.id,
      );
    }

    if (unroutableForResource.length === 0) return null;

    return (
      <StyledMessage
        type="warning"
        overrideIconAlignment="self-start"
        overrideTextAlignment="text-left"
        overridePadding="p-4"
      >
        {{
          icon: (
            <ExclamationTriangleIcon className="h-6 w-6" aria-hidden="true" />
          ),
          message: (
            <div className="ml-3">
              <h3 className="text-sm font-medium">
                {unroutableForResource.length} task
                {unroutableForResource.length > 1 ? "s" : ""} could not be
                routed to meet the service window and work time constraints.
              </h3>
              <div className="mt-2 text-sm">
                <ul className="list-disc space-y-1 pl-5">
                  {unroutableForResource.map((task) => (
                    <li key={task.taskId}>
                      {
                        resourceTasksList.find((t) => t.id === task.taskId)
                          ?.title
                      }{" "}
                      - {task.reason}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ),
        }}
      </StyledMessage>
    );
    // if we hate the orange color of StyledMessage --
    // return (
    //   <div className="mb-4 rounded-md bg-yellow-50 p-4 text-yellow-800">
    //     <div className="flex">
    //       <ExclamationTriangleIcon className="h-6 w-6" aria-hidden="true" />
    //       <div className="ml-3">
    //         <h3 className="text-sm font-medium">
    //           {unroutableForResource.length} task
    //           {unroutableForResource.length > 1 ? "s" : ""} could not be routed
    //           to meet the service window and work time constraints.
    //         </h3>
    //         <div className="mt-2 text-sm">
    //           <ul className="list-disc space-y-1 pl-5">
    //             {unroutableForResource.map((task) => (
    //               <li key={task.taskId}>
    //                 {resourceTasksList.find((t) => t.id === task.taskId)?.title}{" "}
    //                 - {task.reason}
    //               </li>
    //             ))}
    //           </ul>
    //         </div>
    //       </div>
    //     </div>
    //   </div>
    // );
  };

  return (
    <div className="flex h-full flex-grow flex-col space-y-2 pl-1 pt-2">
      <div className="flex flex-row items-center justify-between space-x-2">
        <div className="text-lg font-semibold">
          {selectedResource && (
            <>
              {selectedResource.vehicle &&
                `${selectedResource.vehicle.title} - `}
              {/* {selectedResource.vehicle &&
                selectedResource.technicians &&
                selectedResource.technicians.length > 0 &&
                " - "} */}
              {selectedResource.technicians &&
                selectedResource.technicians.length > 0 &&
                technicianDisplayNames}
            </>
          )}

          {!selectedResource && (
            <div>
              Select a resource from the list on the left
              <hr className="mt-2 w-full border border-gray-100" />
            </div>
          )}
        </div>
        {assignUserButton}
      </div>

      <UnroutableTasksWarning />

      {/* Add the horizontal timeline here */}
      {selectedResource?.vroomRoute && (
        <HorizontalTimeline steps={timelineSteps} />
      )}

      <div className="flex flex-col space-y-2 lg:flex-row lg:space-x-2 lg:space-y-0">
        {/* Map of routes */}
        <div className="h-full w-full border border-gray-200 lg:max-w-xl">
          <MapWithDirections
            key={
              selectedResource?.vehicle?.id ??
              selectedResource?.technicians[0]?.id ??
              "no-resource-map"
            }
            mapCenter={{
              lat: siteKeyDoc?.latitude ?? 0,
              lng: siteKeyDoc?.longitude ?? 0,
            }}
            directionsResponses={directionsResponses}
            children={{
              resourceStartEndMarkers: children.resourceStartEndMarkers,
              resourceMarkers: children.resourceMarkers,
            }}
          />
        </div>

        {/* Timeline replacing Tasks List */}
        <div className="flex max-h-[calc(100vh-50.5rem)] min-h-24 w-full flex-col overflow-y-auto lg:max-h-[calc(100vh-18rem)] lg:max-w-2xl">
          {selectedResource?.vroomRoute ? (
            <CustomTimeline
              steps={timelineSteps}
              onTaskHover={setHoveredTask}
              onTaskClick={setSelectedTask}
            />
          ) : (
            <div className="mt-4 text-center text-gray-500">
              No route information available
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SchedulingDetailResourceView;

interface TimelineStep {
  status: "start" | "driving" | "working" | "waiting" | "break" | "end";
  timestamp: number;
  durationMinutes: number;
  durationString: string;
  task: ExistingTask | null;
  breakTitle?: string;
}

function convertVroomStepsToTimelineSteps(
  steps: VroomStep[],
  tasks: ExistingTask[],
): TimelineStep[] {
  const timelineSteps: TimelineStep[] = [];

  // Steps include driving durations and service durations. We need to break them down into separate steps
  for (const step of steps) {
    if (step.type === "start") {
      timelineSteps.push({
        status: "start",
        timestamp: step.arrival,
        durationMinutes: 0,
        durationString: "",
        task: null,
      });
    }

    if (step.type === "job") {
      // We need to break the job into multiple steps for the driving, working, and waiting durations
      const previousStep = timelineSteps[timelineSteps.length - 1];

      // Because the step.duration is cumulative, we need to calculate the driving duration by the difference of this steps' arrival time and the previous step's completion time
      let previousStepCompletionTime = 0;
      if (previousStep) {
        previousStepCompletionTime =
          previousStep.timestamp + previousStep.durationMinutes * 60;
      }
      const drivingDurationSeconds = step.arrival - previousStepCompletionTime;

      // Readable duration in minutes
      const drivingDurationReadable = `${Math.floor(drivingDurationSeconds / 60)} minutes`;
      timelineSteps.push({
        status: "driving",
        timestamp: step.arrival - drivingDurationSeconds,
        durationMinutes: drivingDurationSeconds / 60,
        durationString: drivingDurationReadable,
        task: null,
      });

      // Waiting step
      let waitingDurationSeconds = 0;
      if (step.waiting_time > 0) {
        waitingDurationSeconds = step.waiting_time;
        const waitingDurationReadable = `${Math.floor(waitingDurationSeconds / 60)} minutes`;
        timelineSteps.push({
          status: "waiting",
          timestamp: step.arrival,
          durationMinutes: waitingDurationSeconds / 60,
          durationString: waitingDurationReadable,
          task: null,
        });
      }

      // Working step
      const workingDurationSeconds = step.service;
      const workingDurationReadable = `${Math.floor(workingDurationSeconds / 60)} minutes`;
      timelineSteps.push({
        status: "working",
        timestamp: step.arrival + waitingDurationSeconds,
        durationMinutes: workingDurationSeconds / 60,
        durationString: workingDurationReadable,
        task: tasks.find((task) => task.id === step.description) ?? null,
      });
    }

    // Break step
    if (step.type === "break") {
      const breakDurationSeconds = step.waiting_time;
      const breakDurationReadable = `${Math.floor(breakDurationSeconds / 60)} minutes`;
      timelineSteps.push({
        status: "break",
        timestamp: step.arrival,
        durationMinutes: breakDurationSeconds / 60,
        durationString: breakDurationReadable,
        task: null,
        breakTitle: step.description,
      });
    }

    // End step
    if (step.type === "end") {
      // End step will also have a driving step that needs to be created first before the end step
      const previousStep = timelineSteps[timelineSteps.length - 1];
      let previousStepCompletionTime = 0;
      if (previousStep) {
        previousStepCompletionTime =
          previousStep.timestamp + previousStep.durationMinutes * 60;
      }
      const drivingDurationSeconds = step.arrival - previousStepCompletionTime;
      timelineSteps.push({
        status: "driving",
        timestamp: step.arrival - drivingDurationSeconds,
        durationMinutes: drivingDurationSeconds / 60,
        durationString: `${Math.floor(drivingDurationSeconds / 60)} minutes`,
        task: null,
      });

      // End step
      timelineSteps.push({
        status: "end",
        timestamp: step.arrival,
        durationMinutes: 0,
        durationString: "",
        task: null,
      });
    }
  }
  return timelineSteps;
}

const CustomTimeline: React.FC<{
  steps: TimelineStep[];
  onTaskHover: (taskId: string | null) => void;
  onTaskClick: (task: ExistingTask | null) => void;
}> = ({ steps, onTaskHover, onTaskClick }) => {
  return (
    <div className="relative flex h-full">
      {/* Timeline line */}
      <div className="absolute bottom-8 left-[18px] top-8 w-0.5 bg-gray-200" />

      <div className="relative ml-16 flex-grow">
        {steps.map((step, index) => {
          const isWorking = step.status === "working";

          return (
            <motion.div
              key={index}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.3, delay: index * 0.1 }}
              className="relative mb-4"
            >
              {/* Timeline dot and line segment */}
              <div className="absolute -left-[52px] top-3 flex items-center">
                <div
                  className={`h-4 w-4 rounded-full ${
                    isWorking ? "bg-indigo-600" : "bg-blue-400"
                  } flex items-center justify-center`}
                >
                  {isWorking ? (
                    <FaTools className="h-2 w-2 text-white" />
                  ) : (
                    <FaCar className="h-2 w-2 text-white" />
                  )}
                </div>
                <div
                  className={`-ml-0.5 h-0.5 w-8 ${
                    isWorking ? "bg-indigo-600" : "bg-blue-400"
                  }`}
                />
              </div>

              {/* Content card */}
              <div
                className={`ml-2 rounded-lg p-2 transition-all duration-200 ${
                  isWorking
                    ? "bg-gray-50 hover:bg-gray-100"
                    : "bg-white hover:bg-gray-50"
                } shadow-sm`}
                onMouseEnter={() => step.task && onTaskHover(step.task.id)}
                onMouseLeave={() => onTaskHover(null)}
                onClick={() => step.task && onTaskClick(step.task)}
              >
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="text-sm font-medium">
                      {getTitleForTimelineStep(step)}
                    </h3>
                    {(step.status === "working" ||
                      step.status === "driving" ||
                      step.status === "waiting" ||
                      step.status === "break") && (
                      <p className="text-xs text-gray-500">
                        Start:{" "}
                        {convertToReadableTimestampTime(
                          Timestamp.fromMillis(step.timestamp * 1000),
                        )}
                      </p>
                    )}
                    {/* If it's a job/task, show the service window start and end times */}
                    {step.task && (
                      <>
                        <p className="text-xs text-gray-500">
                          Service Window:{" "}
                          {convertToReadableTimestampTime(
                            step.task.taskSpecificDetails[
                              "scheduledServiceWindowStart"
                            ],
                          )}{" "}
                          -{" "}
                          {convertToReadableTimestampTime(
                            step.task.taskSpecificDetails[
                              "scheduledServiceWindowEnd"
                            ],
                          )}
                        </p>
                      </>
                    )}
                    {step.durationMinutes > 0 && step.status === "working" && (
                      <p className="text-xs text-gray-500">
                        Duration: {step.durationString}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
};

function getTitleForTimelineStep(step: TimelineStep): string {
  if (step.status === "working" && step.task) {
    return step.task.title;
  }

  if (step.status === "driving") {
    return "Driving - " + step.durationString;
  }

  if (step.status === "waiting") {
    return "Waiting - " + step.durationString;
  }

  if (step.status === "break") {
    return step.breakTitle + " - " + step.durationString;
  }

  if (step.status === "end") {
    return (
      "End - " +
      convertToReadableTimestampTime(
        Timestamp.fromMillis(step.timestamp * 1000),
      )
    );
  }

  if (step.status === "start") {
    return (
      "Start - " +
      convertToReadableTimestampTime(
        Timestamp.fromMillis(step.timestamp * 1000),
      )
    );
  }

  return step.status.charAt(0).toUpperCase() + step.status.slice(1);
}

interface TimelineSegmentProps {
  step: TimelineStep;
  startOfDay: number; // timestamp for 12:00 AM
  endOfDay: number; // timestamp for 11:59 PM
  totalWidth: number; // width of timeline in pixels
}

const HorizontalTimeline: React.FC<{
  steps: TimelineStep[];
}> = ({ steps }) => {
  // Get start of day (6:00 AM) and end time (2 hours after last step)
  const firstStep = steps[0];
  const lastStep = steps[steps.length - 1];
  if (!firstStep || !lastStep) return null;

  const startOfDay = new Date(firstStep.timestamp * 1000);
  startOfDay.setHours(6, 0, 0, 0); // Set to 6 AM

  const endOfDay = new Date(lastStep.timestamp * 1000);
  endOfDay.setHours(endOfDay.getHours() + 2, 0, 0, 0); // Set to 2 hours after last step

  const startTimestamp = startOfDay.getTime() / 1000;
  const endTimestamp = endOfDay.getTime() / 1000;
  const totalSeconds = endTimestamp - startTimestamp;
  const totalHours = Math.ceil((endTimestamp - startTimestamp) / 3600);

  return (
    <div className="mb-4 w-full px-4">
      <div className="relative pt-4">
        {/* Time markers - Moved outside the timeline bar */}
        <div className="absolute left-0 right-0 top-0 h-6">
          {/* Generate markers for each hour */}
          {Array.from({ length: totalHours + 1 }).map((_, i) => {
            const hour = i + 6; // Start from 6 AM
            const hourPosition = (i / totalHours) * 100;
            return (
              <div
                key={i}
                className="absolute bottom-0 top-0"
                style={{ left: `${hourPosition}%` }}
              >
                <span className="absolute -top-1 -translate-x-1/2 whitespace-nowrap text-xs text-gray-500">
                  {hour >= 24
                    ? `${hour - 24} AM`
                    : hour > 12
                      ? `${hour - 12} PM`
                      : hour === 12
                        ? "12 PM"
                        : `${hour} AM`}
                </span>
              </div>
            );
          })}
        </div>

        {/* Timeline bar */}
        <div className="relative h-8 rounded-lg bg-gray-100">
          {/* Vertical time marker lines */}
          {Array.from({ length: totalHours + 1 }).map((_, i) => (
            <div
              key={i}
              className="absolute bottom-0 top-0 border-l border-gray-300"
              style={{ left: `${(i / totalHours) * 100}%` }}
            />
          ))}

          {/* Timeline segments */}
          {steps.map((step, index) => {
            const segmentStart = Math.max(
              0,
              (step.timestamp - startTimestamp) / totalSeconds,
            );
            const segmentEnd =
              step.durationMinutes > 0
                ? Math.min(
                    1,
                    (step.timestamp +
                      step.durationMinutes * 60 -
                      startTimestamp) /
                      totalSeconds,
                  )
                : segmentStart;

            // Skip segments that are completely outside our time window
            if (segmentStart > 1 || segmentEnd < 0) return null;

            const getSegmentColor = () => {
              switch (step.status) {
                case "driving":
                  return "bg-blue-400";
                case "working":
                  return "bg-green-600";
                case "waiting":
                  return "bg-yellow-400";
                case "break":
                  return "bg-gray-400";
                default:
                  return "bg-gray-300";
              }
            };

            return (
              <div
                key={index}
                className={`absolute bottom-0 top-0 ${getSegmentColor()} group cursor-pointer transition-opacity hover:opacity-90`}
                style={{
                  left: `${segmentStart * 100}%`,
                  width: `${Math.max((segmentEnd - segmentStart) * 100, 0.5)}%`,
                }}
              >
                {/* Tooltip with pointer-events-none on wrapper to prevent flickering */}
                <div className="pointer-events-none absolute bottom-full left-1/2 mb-2 -translate-x-1/2">
                  <div className="hidden group-hover:block">
                    <div className="pointer-events-auto whitespace-nowrap rounded-lg border border-gray-200 bg-white p-2 text-xs shadow-lg">
                      <div className="font-medium">
                        {getTitleForTimelineStep(step)}
                      </div>
                      {(step.status === "working" ||
                        step.status === "driving" ||
                        step.status === "waiting" ||
                        step.status === "break") && (
                        <div className="text-gray-500">
                          Start:{" "}
                          {convertToReadableTimestampTime(
                            Timestamp.fromMillis(step.timestamp * 1000),
                          )}
                        </div>
                      )}
                      {step.task && (
                        <div className="text-gray-500">
                          Service Window:{" "}
                          {convertToReadableTimestampTime(
                            step.task.taskSpecificDetails[
                              "scheduledServiceWindowStart"
                            ],
                          )}{" "}
                          -{" "}
                          {convertToReadableTimestampTime(
                            step.task.taskSpecificDetails[
                              "scheduledServiceWindowEnd"
                            ],
                          )}
                        </div>
                      )}
                      {step.durationMinutes > 0 && (
                        <div className="text-gray-500">
                          Duration: {step.durationString}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
